import { useEffect, useRef, useState } from "react";
import {
  bookCreationProfileForTemplate,
  type BookTemplate,
  type CreationProductionFamily,
} from "@lifescapsule/contracts";
import { Icon } from "../../shared/Icon";
import { useAudioExperience } from "../../shared/AudioExperience";
import { getFinishedBookExample } from "./examples";

type PreviewLanguage = {
  transformationLabel: string;
  sourceLabel: string;
  processLabel: string;
  keptLabel: string;
  unitLabel: string;
  sourceHeading: string;
  sourceFootnote: string;
  companionHeading: string;
  companionTitle: string;
  companionBody: string;
  companionFootnote: string;
  primaryControl: string;
  secondaryControl: string;
  replayLabel: string;
  usesVoiceKeepsake: boolean;
};

const previewLanguage: Record<CreationProductionFamily, PreviewLanguage> = {
  NARRATIVE: {
    transformationLabel: "From spoken memory to printed story",
    sourceLabel: "spoken memory",
    processLabel: "shaped with care",
    keptLabel: "kept in print",
    unitLabel: "Sample chapter",
    sourceHeading: "The question that opened the door",
    sourceFootnote: "Original recording preserved privately",
    companionHeading: "The voice behind the page",
    companionTitle: "Hear this memory",
    companionBody:
      "An optional, revocable QR link can reconnect the printed words with the storyteller’s original voice.",
    companionFootnote: "Illustrative QR · no customer media",
    primaryControl: "Chapter spread",
    secondaryControl: "Voice keepsake",
    replayLabel: "Replay memory transformation",
    usesVoiceKeepsake: true,
  },
  PICTURE_BOOK: {
    transformationLabel: "From family story seed to illustrated page turn",
    sourceLabel: "family story seed",
    processLabel: "shaped into page turns",
    keptLabel: "ready to read aloud",
    unitLabel: "Sample page turn",
    sourceHeading: "The memory behind this scene",
    sourceFootnote: "Family direction held beside the story plan",
    companionHeading: "A read-aloud invitation",
    companionTitle: "Join the page turn",
    companionBody:
      "A gentle prompt can invite the reader to whisper, point, wonder, or choose what happens next.",
    companionFootnote: "Fictional participation cue · no customer media",
    primaryControl: "Story spread",
    secondaryControl: "Reader moment",
    replayLabel: "Replay story transformation",
    usesVoiceKeepsake: false,
  },
  RECIPE: {
    transformationLabel: "From family recipe to structured culinary keepsake",
    sourceLabel: "family recipe",
    processLabel: "structured with care",
    keptLabel: "ready for the kitchen",
    unitLabel: "Sample recipe",
    sourceHeading: "The detail behind the dish",
    sourceFootnote: "Family notes held beside the structured recipe",
    companionHeading: "Cook’s note",
    companionTitle: "Why one clove more",
    companionBody:
      "The method, substitutions, serving ritual, and family variations stay beside the ingredient list.",
    companionFootnote: "Fictional recipe concept · no customer media",
    primaryControl: "Recipe spread",
    secondaryControl: "Family notes",
    replayLabel: "Replay recipe transformation",
    usesVoiceKeepsake: false,
  },
  COMIC_CORE: {
    transformationLabel: "From true family scene to sequential-art page",
    sourceLabel: "true family scene",
    processLabel: "shaped into panels",
    keptLabel: "ready to read",
    unitLabel: "Sample scene",
    sourceHeading: "The beat behind the panels",
    sourceFootnote: "Scene direction held beside the sequential art",
    companionHeading: "Next panel",
    companionTitle: "The turn in the scene",
    companionBody:
      "Captions, expressions, and balloons carry the action forward while keeping the real event recognizable.",
    companionFootnote: "Fictional sequential-art concept · no customer media",
    primaryControl: "Six-panel scene",
    secondaryControl: "Scene notes",
    replayLabel: "Replay scene transformation",
    usesVoiceKeepsake: false,
  },
};

export function FinishedBookPreview({ template }: { template: BookTemplate }) {
  const example = getFinishedBookExample(template.id);
  const productionFamily =
    bookCreationProfileForTemplate(template.id)?.productionFamily ?? "NARRATIVE";
  const language = previewLanguage[productionFamily];
  const [spread, setSpread] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { play } = useAudioExperience();

  useEffect(() => {
    const node = rootRef.current;
    if (!node || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  function changeSpread(next: number) {
    if (next === spread) return;
    setSpread(next);
    play("page-turn");
  }

  function replayTransformation() {
    setRevealed(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setRevealed(true));
    });
    play("reveal");
  }

  return (
    <div
      ref={rootRef}
      className={`finished-preview ${revealed ? "finished-preview--revealed" : ""}`}
      style={
        {
          "--preview-cover": template.palette.cover,
          "--preview-ink": template.palette.coverText,
          "--preview-accent": template.palette.accent,
        } as React.CSSProperties
      }
    >
      <div className="memory-transformation" role="group" aria-label={language.transformationLabel}>
        <div className="memory-transformation__source">
          <span className="memory-transformation__play">
            <Icon name={language.usesVoiceKeepsake ? "mic" : "spark"} size={17} />
          </span>
          <p>“{example.opening.split(".")[0]}.”</p>
          <small>{language.sourceLabel}</small>
        </div>
        <span className="memory-transformation__thread" aria-hidden="true" />
        <div className="memory-transformation__chapter">
          <i />
          <i />
          <i />
          <small>{language.processLabel}</small>
        </div>
        <span className="memory-transformation__thread" aria-hidden="true" />
        <div className="memory-transformation__kept">
          <Icon name="book" size={20} />
          <small>{language.keptLabel}</small>
        </div>
      </div>

      <div className={`open-book open-book--${example.visual}`} key={spread}>
        <div className="open-book__gutter" />
        {spread === 0 ? (
          <>
            <article className="open-book__page open-book__page--left">
              <span className="open-book__folio">Life’s Capsule · {template.shortName}</span>
              <div className={`example-art example-art--${example.visual}`} aria-hidden="true">
                <span />
                <i />
              </div>
              <small>{example.detail}</small>
            </article>
            <article className="open-book__page open-book__page--right">
              <span className="open-book__chapter">{language.unitLabel}</span>
              <h3>{example.chapter}</h3>
              <p className="open-book__opening">{example.opening}</p>
              <blockquote>“{example.quote}”</blockquote>
              <span className="open-book__page-number">24</span>
            </article>
          </>
        ) : (
          <>
            <article className="open-book__page open-book__page--left open-book__source-page">
              <span className="open-book__chapter">{language.sourceHeading}</span>
              <blockquote>“{example.sourcePrompt}”</blockquote>
              <div className="open-book__wave" aria-hidden="true">
                {Array.from({ length: 25 }, (_, index) => (
                  <i key={index} style={{ height: `${22 + ((index * 19) % 58)}%` }} />
                ))}
              </div>
              <small>{language.sourceFootnote}</small>
            </article>
            <article className="open-book__page open-book__page--right open-book__qr-page">
              <span className="open-book__chapter">{language.companionHeading}</span>
              {language.usesVoiceKeepsake ? (
                <div className="sample-qr" aria-hidden="true">
                  <span />
                  <i />
                  <b />
                </div>
              ) : (
                <Icon name="spark" size={58} />
              )}
              <h3>{language.companionTitle}</h3>
              <p>{language.companionBody}</p>
              <small>{language.companionFootnote}</small>
              <span className="open-book__page-number">25</span>
            </article>
          </>
        )}
      </div>

      <div className="finished-preview__controls">
        <div className="spread-switch" role="group" aria-label="Example book spread">
          <button
            type="button"
            className={spread === 0 ? "active" : ""}
            onClick={() => changeSpread(0)}
            aria-pressed={spread === 0}
          >
            {language.primaryControl}
          </button>
          <button
            type="button"
            className={spread === 1 ? "active" : ""}
            onClick={() => changeSpread(1)}
            aria-pressed={spread === 1}
          >
            {language.secondaryControl}
          </button>
        </div>
        <button className="replay-button" type="button" onClick={replayTransformation}>
          <Icon name="spark" size={17} /> {language.replayLabel}
        </button>
      </div>
    </div>
  );
}
